import { Hero } from '../campaigns/hero';
import styles from './portfolio.module.css';
import { Photo } from '../../../components/photo';
import { Label } from '../../../components/label';
import { ProgressPill } from '../progress-pill';
import { Card } from '../../../components/card';
import { PageHead } from '../../../components/page-head';
// student-portfolio-page.tsx — Resume-style student portfolio (elite-purple).
// Cover hero (avatar + identity + stats), journey rows, and a sidebar with
// skills practiced, endorsements and badges. Bio is inline-editable.
import { Button } from '../../../components/button';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import defaultAvatar from '../../../assets/icons/avatars/default-avatar.svg';
import { getAchievementImage } from '../../../assets/achievements/achievement-images';
import { ACHIEVEMENT_REGISTRY } from '../../../constants/achievement-registry';
import { useStudentPortfolio, useSetBio } from '../../../api/student/portfolio/hooks';
import { useStudentProgress } from '../../../api/student/progress/hooks';
import type {
  PortfolioCampaign,
  PortfolioSkill,
  PortfolioBadge,
  PortfolioEndorsement,
  StudentPortfolio,
} from '../../../api/student/portfolio/types';
import type { StudentNavigate } from '../student-location';
import stat from '../stat.module.css';
import { endorsementTag } from '../endorsement-tag';

const MAX_BIO_LENGTH = 500;

export function StudentPortfolioPage({ navigate }: { navigate: StudentNavigate }) {
  const { t } = useTranslation();
  const portfolioQuery = useStudentPortfolio();

  if (portfolioQuery.isLoading) {
    return <div className="page page-state">{t('student.portfolio.loading')}</div>;
  }
  if (portfolioQuery.isError || !portfolioQuery.data) {
    return <div className="page page-state">{t('student.portfolio.loadError')}</div>;
  }

  const portfolio: StudentPortfolio = portfolioQuery.data;

  return (
    <div className="page">
      <PortfolioHeader portfolio={portfolio} />
      <div className={`${styles.pfLayout}`}>
        <div>
          <CampaignsSection campaigns={portfolio.campaigns} navigate={navigate} />
        </div>
        <aside>
          <SkillsPanel skills={portfolio.skills} />
          <EndorsementsPanel endorsements={portfolio.endorsements} />
          <BadgesPanel badges={portfolio.badges} />
        </aside>
      </div>
    </div>
  );
}

// ── Cover hero (identity + top stats) ─────────────────────────────────────

function PortfolioHeader({ portfolio }: { portfolio: StudentPortfolio }) {
  const { t } = useTranslation();
  const { student } = portfolio;
  const progress = useStudentProgress().data;
  const setBio = useSetBio();
  const savingBio = setBio.isPending;

  const [editingBio, setEditingBio] = useState(false);
  const [bioDraft, setBioDraft] = useState('');

  function openBioEditor() {
    setBioDraft(student.bio ?? '');
    setEditingBio(true);
  }

  async function saveBio() {
    try {
      await setBio.mutateAsync(bioDraft);
      setEditingBio(false);
    } catch {
      // keep the editor open so the student can retry
    }
  }

  const metricValue = (key: string) =>
    portfolio.ponderImpact.find((m) => m.key === key)?.value ?? 0;

  return (
    <Hero
      size="portfolio"
      leading={<img src={student.avatarPhotoUrl || defaultAvatar} alt={student.name} className={styles.pfAva} />}
      badge={<Label>{t('student.portfolio.title')}</Label>}
      title={student.name}
    >
          <p className={`${styles.pfRole}`}>
            {progress ? t('student.hero.level', { level: progress.level }) : ''}
            {progress && student.email ? ' · ' : ''}
            {student.email}
          </p>

          {editingBio ? (
            <div className={`${styles.pfBioEdit}`}>
              <textarea
                value={bioDraft}
                onChange={(e) => setBioDraft(e.target.value)}
                maxLength={MAX_BIO_LENGTH}
                rows={3}
                className={`${styles.pfBioTextarea}`}
                placeholder={t('student.portfolio.bioPlaceholder')}
                autoFocus
              />
              <div className={`${styles.pfBioActions}`}>
                <Button onClick={saveBio} disabled={savingBio}>
                  {t('student.portfolio.saveBio')}
                </Button>
                <Button variant="gold" onClick={() => setEditingBio(false)} disabled={savingBio}>
                  {t('student.portfolio.cancelBio')}
                </Button>
              </div>
            </div>
          ) : (
            <p className={`${styles.pfBio}`}>
              <span className={`${styles.pfBioText}`}>{student.bio?.trim() || t('student.portfolio.noBio')}</span>
              <button
                type="button"
                className={`${styles.pfBioEditBtn}`}
                onClick={openBioEditor}
                aria-label={t('student.portfolio.editBio')}
                title={t('student.portfolio.editBio')}
              >
                ✏️
              </button>
            </p>
          )}

          <div className={`${styles.pfStats}`}>
            <HeaderStat value={`${progress?.xpPoints ?? 0} XP`} label={t('student.home.totalXp')} />
            <HeaderStat value={metricValue('campaignsCompleted')} label={t('student.portfolio.headerStats.campaignsCompleted')} />
            <HeaderStat value={portfolio.badges.length} label={t('student.portfolio.badgesTitle')} />
          </div>
    </Hero>
  );
}

function HeaderStat({ value, label }: { value: string | number; label: string }) {
  return (
    <div>
      <span className={stat.statLabel}>{label}</span>
      <span className={stat.statValue}>{value}</span>
    </div>
  );
}

// ── Journey ───────────────────────────────────────────────────────────────

function CampaignsSection({ campaigns, navigate }: { campaigns: PortfolioCampaign[]; navigate: StudentNavigate }) {
  const { t } = useTranslation();
  // Most recent first (latest start date at the top).
  const ordered = [...campaigns].sort(
    (a, b) => (b.startDateUtc ?? Number.NEGATIVE_INFINITY) - (a.startDateUtc ?? Number.NEGATIVE_INFINITY),
  );
  return (
    <section>
      <PageHead level="section">{t('student.portfolio.campaignsTitle')}</PageHead>
      {ordered.length === 0 ? (
        <p className={`${styles.pfEmpty}`}>{t('student.portfolio.campaignsEmpty')}</p>
      ) : (
        ordered.map((c) => <CampaignCard key={c.id} campaign={c} navigate={navigate} />)
      )}
    </section>
  );
}

function CampaignCard({ campaign, navigate }: { campaign: PortfolioCampaign; navigate: StudentNavigate }) {
  const { t, i18n } = useTranslation();
  // The portfolio records the student's unfinished work, which remains
  // available after live sessions end. Only their completion changes this
  // status; campaign dates intentionally do not (Matt, 4 Oct 2026).
  const statusKey = campaign.completedAt ? 'completed' : 'active';
  const dateRange = formatDateRange(i18n.language, campaign.startDateUtc, campaign.endDateUtc);
  const metaParts = [
    dateRange,
    campaign.teamSize != null
      ? `${t('student.portfolio.team', { count: campaign.teamSize })}${campaign.teamName ? ` · ${campaign.teamName}` : ''}`
      : null,
  ].filter(Boolean);

  return (
    <Card as="article" framed padded={false} className={`${styles.jrow}`}>
      <Photo className={`${styles.jrow__art}`} src={campaign.imageUrl} />
      <div className={`${styles.jrow__body}`}>
        <h3 className={`${styles.jrow__title}`}>{campaign.name}</h3>
        {metaParts.length > 0 && <p className={`${styles.jrow__meta}`}>{metaParts.join(' · ')}</p>}
        {campaign.accomplishments.length === 0 ? (
          <p className={`${styles.jrow__noacc}`}>{t('student.portfolio.noAccomplishments')}</p>
        ) : (
          <ul className={`${styles.jrow__list}`}>
            {campaign.accomplishments.map((a) => (
              <li key={a.id}>{a.text}</li>
            ))}
          </ul>
        )}
        {campaign.certificateEarned && (
          <button
            type="button"
            className={`${styles.jrow__cert}`}
            onClick={() => navigate({ kind: 'campaign', campaignId: campaign.id, tab: 'certificate' })}
          >
            🏅 {t('student.portfolio.viewCertificate')}
          </button>
        )}
      </div>
      <ProgressPill of="campaign" status={statusKey} />
    </Card>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────

const SIDEBAR_INITIAL_LIMIT = 4;

function SkillsPanel({ skills }: { skills: PortfolioSkill[] }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const practiced = [...skills].filter((s) => s.points > 0).sort((a, b) => b.points - a.points);
  const visible = expanded ? practiced : practiced.slice(0, SIDEBAR_INITIAL_LIMIT);
  const hiddenCount = practiced.length - SIDEBAR_INITIAL_LIMIT;
  return (
    <Card as="section" framed padded={false} className={`${styles.side}`}>
      <h3>{t('student.portfolio.skillsTitle')}</h3>
      <p className={styles.micro}>{t('student.portfolio.skillsSubtitle')}</p>
      {practiced.length === 0 ? (
        <p className={`${styles.pfEmpty}`}>{t('student.portfolio.skillsEmpty')}</p>
      ) : (
        <>
          {visible.map((s) => (
            <div key={s.skillId} className={`${styles.skill}`}>
              <span className={`${styles.skill__name}`}>{t(`skills.${s.skillId}.name`, { defaultValue: s.skillId })}</span>
              <span className={`${styles.skill__n}`}>{s.points}<small>{t('student.portfolio.skillUses')}</small></span>
            </div>
          ))}
          {hiddenCount > 0 && (
            <button type="button" className={`${styles.side__toggle}`} onClick={() => setExpanded((p) => !p)}>
              {expanded ? t('student.portfolio.showLess') : t('student.portfolio.showMore', { count: hiddenCount })}
            </button>
          )}
        </>
      )}
    </Card>
  );
}

function EndorsementsPanel({ endorsements }: { endorsements: PortfolioEndorsement[] }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const earned = [...endorsements].filter((e) => e.count > 0).sort((a, b) => b.count - a.count);
  if (earned.length === 0) return null;
  const visible = expanded ? earned : earned.slice(0, SIDEBAR_INITIAL_LIMIT);
  const hiddenCount = earned.length - SIDEBAR_INITIAL_LIMIT;
  return (
    <Card as="section" framed padded={false} className={`${styles.side}`}>
      <h3>{t('student.portfolio.endorsementsTitle')}</h3>
      <p className={styles.micro}>{t('student.portfolio.endorsementsSubtitle')}</p>
      <div className={`${styles.endorse}`}>
        {visible.map((e) => (
          <span key={e.tagId} className={`${styles.endorse__chip}`}>
            {endorsementTag(t, e.tagId).name}
            <span className={`${styles.endorse__count}`}>×{e.count}</span>
          </span>
        ))}
      </div>
      {hiddenCount > 0 && (
        <button type="button" className={`${styles.side__toggle}`} onClick={() => setExpanded((p) => !p)}>
          {expanded ? t('student.portfolio.showLess') : t('student.portfolio.showMore', { count: hiddenCount })}
        </button>
      )}
    </Card>
  );
}

function BadgesPanel({ badges }: { badges: PortfolioBadge[] }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const sorted = [...badges].sort(
    (a, b) => new Date(b.awardedAt).getTime() - new Date(a.awardedAt).getTime(),
  );
  const visible = expanded ? sorted : sorted.slice(0, SIDEBAR_INITIAL_LIMIT * 2);
  const hiddenCount = sorted.length - SIDEBAR_INITIAL_LIMIT * 2;
  return (
    <Card as="section" framed padded={false} className={`${styles.side}`}>
      <h3>{t('student.portfolio.badgesTitle')}</h3>
      {sorted.length === 0 ? (
        <p className={`${styles.pfEmpty}`}>{t('student.portfolio.noBadges')}</p>
      ) : (
        <>
          <div className={`${styles.medals}`}>
            {visible.map((b) => {
              const def = ACHIEVEMENT_REGISTRY[b.achievementId];
              const image = def ? getAchievementImage(def.imageId) : undefined;
              const nameKey = def ? def.nameKey : b.achievementId;
              const name = t(nameKey, { defaultValue: b.achievementId });
              return (
                <div key={b.achievementId} className={`${styles.medal}`} title={name}>
                  {image ? <img src={image} alt="" /> : <span>◆</span>}
                  <span className={`${styles.medal__name}`}>{name}</span>
                </div>
              );
            })}
          </div>
          {hiddenCount > 0 && (
            <button type="button" className={`${styles.side__toggle}`} onClick={() => setExpanded((p) => !p)}>
              {expanded ? t('student.portfolio.showLess') : t('student.portfolio.showMore', { count: hiddenCount })}
            </button>
          )}
        </>
      )}
    </Card>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

/** Format a campaign's start–end window from Unix-second timestamps. */
function formatDateRange(lang: string, startUtc: number | null, endUtc: number | null): string | null {
  if (!startUtc && !endUtc) return null;
  const fmt = new Intl.DateTimeFormat(lang, { month: 'short', year: 'numeric' });
  const start = startUtc ? fmt.format(new Date(startUtc * 1000)) : null;
  const end = endUtc ? fmt.format(new Date(endUtc * 1000)) : null;
  if (start && end) return start === end ? start : `${start} – ${end}`;
  return start ?? end;
}
